<template>
    <div class="listing-settings__columns">
        <p class="listing-settings__columns-title typographies-paragraph-medium-regular">
            {{ $t.__('listing.settings.ListingSettings.tabs.columns.description') }}
        </p>

        <div class="listing-settings__columns-container">
            <div v-if="searchable" class="listing-settings__columns-search">
                <CompassInput
                    v-model="searchQuery"
                    :placeholder="$t.__('common.actions.search_column')"
                    class="listing-settings__columns-search-input"
                    prefixIcon="search"
                >
                    <template #suffix>
                        <CompassIconButton
                            v-show="hasSearchQuery"
                            class="listing-settings__columns-search-reset"
                            icon="close"
                            size="small"
                            @click="searchQuery = ''"
                        />
                    </template>
                </CompassInput>

                <p
                    v-if="hasSearchQuery"
                    class="listing-settings__columns-search-catpion typographies-paragraph-medium-regular"
                >
                    {{
                        hasSearchResults
                            ? $t.__('common.labels.search_results')
                            : $t.__('common.labels.no_match_found')
                    }}
                </p>
            </div>

            <ListingSettingsColumnItem
                v-for="column in columnsFixedStart"
                :key="column.code"
                :class="getColumnClass(column)"
                :column="column"
                :hasSearchQuery="hasSearchQuery"
                @checkboxChange="
                    () => {
                        column.isVisible = !column.isVisible;
                    }
                "
            />

            <SaasDraggable
                :key="draggableKey"
                v-model="columnsDraggable"
                :disabled="hasSearchQuery"
                filterClasses=".compass-checkbox, .listing-settings-column-item__actions-order"
                dragClass="listing-settings-column-item--draggable"
            >
                <ListingSettingsColumnItem
                    v-for="(column, index) in columnsDraggable"
                    :key="column.code"
                    :canBeMovedDown="canBeMovedDown(index)"
                    :canBeMovedUp="canBeMovedUp(index)"
                    :class="getColumnClass(column)"
                    :column="column"
                    :hasSearchQuery="hasSearchQuery"
                    @checkboxChange="
                        () => {
                            column.isVisible = !column.isVisible;
                            draggableKeyCount++;
                        }
                    "
                    @mooveDown="mooveDown(column)"
                    @mooveUp="mooveUp(column)"
                />
            </SaasDraggable>

            <ListingSettingsColumnItem
                v-for="column in columnsFixedEnd"
                :key="column.code"
                :class="getColumnClass(column)"
                :column="column"
                :hasSearchQuery="hasSearchQuery"
                @checkboxChange="
                    () => {
                        column.isVisible = !column.isVisible;
                    }
                "
            />
        </div>
    </div>
</template>

<script>
import SaasDraggable from 'vcomponents/common/SaasDraggable.vue';
import ListingSettingsColumnItem from 'vcomponents/common/listing/settings/ListingSettingsColumnItem.vue';

export default {
    components: {
        SaasDraggable,
        ListingSettingsColumnItem,
    },
    props: {
        columns: {
            type: Array,
            required: true,
        },
        searchable: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            searchQuery: '',
            draggableKeyCount: 0,
        };
    },
    computed: {
        draggableKey() {
            return (
                this.columns
                    .filter((column) => column.isMovable)
                    .map((column) => column.code)
                    .join('-') + `-${this.draggableKeyCount}`
            );
        },
        hasSearchQuery() {
            return this.searchQuery.length > 0;
        },
        hasSearchResults() {
            return this.hasSearchQuery && this.columnsDisplayed.length > 0;
        },
        columnsDisplayed() {
            let columnToDisplay = this.columns;

            if (this.hasSearchQuery) {
                columnToDisplay = columnToDisplay.filter((column) => {
                    const columnLabelFormatted = _.deburr(column.label.toLowerCase());
                    const searchQueryFormatted = _.deburr(this.searchQuery.toLowerCase());

                    return columnLabelFormatted.includes(searchQueryFormatted);
                });
            }

            return columnToDisplay.sort((columnA, columnB) => {
                if (columnA.order < columnB.order) {
                    return -1;
                } else if (columnA.order > columnB.order) {
                    return 1;
                } else {
                    return 0;
                }
            });
        },
        columnsFixedStart() {
            const columnsFixed = [];
            for (const column of this.columnsDisplayed) {
                if (!column.isMovable) {
                    columnsFixed.push(column);
                } else {
                    break;
                }
            }

            return columnsFixed;
        },
        columnsFixedEnd() {
            const columnsFixed = [];
            if (this.columnsDisplayed.some((column) => column.isMovable)) {
                for (const column of [...this.columnsDisplayed].reverse()) {
                    if (!column.isMovable) {
                        columnsFixed.push(column);
                    } else {
                        break;
                    }
                }
            }

            return columnsFixed.reverse();
        },
        columnsDraggable: {
            get() {
                return this.columnsDisplayed.filter((column) => column.isMovable);
            },
            set(newColumnsDraggable) {
                this.draggableKeyCount = 0;
                const startOrder = this.columnsFixedStart.length;
                for (const [index, newColumn] of newColumnsDraggable.entries()) {
                    newColumn.order = startOrder + index;

                    for (const column of this.columns) {
                        if (column.code === newColumn.code) {
                            column.order = newColumn.order;
                            break;
                        }
                    }
                }
            },
        },
    },
    methods: {
        getColumnClass(column) {
            return {
                'listing-settings-column-item': true,
                'listing-settings-column-item--disabled': column.isDefault && !column.isMovable,
                'listing-settings-column-item--draggable': column.isMovable,
            };
        },
        canBeMovedUp(indexToCheck) {
            let firstIndexAvailable = 0;
            for (const [index, column] of this.columnsDraggable.entries()) {
                if (firstIndexAvailable <= index && !column.isMovable) {
                    firstIndexAvailable = index + 1;
                } else {
                    break;
                }
            }

            return indexToCheck > firstIndexAvailable;
        },
        canBeMovedDown(indexToCheck) {
            let lastIndexAvailable = 0;
            for (const [index, column] of this.columnsDraggable.entries()) {
                if (lastIndexAvailable <= index && column.isMovable) {
                    lastIndexAvailable = index;
                }
            }

            return indexToCheck < lastIndexAvailable;
        },
        mooveUp(columnToMove) {
            const columnsReverse = [...this.columns].reverse();
            let newOrder = columnToMove.order - 1;
            for (const column of columnsReverse) {
                if (column.order === newOrder) {
                    if (column.isMovable) {
                        column.order = columnToMove.order;
                        columnToMove.order = newOrder;
                        break;
                    } else {
                        newOrder--;
                    }
                }
            }

            this.$emit('updateColumns', columnsReverse.reverse());
        },
        mooveDown(columnToMove) {
            let newOrder = columnToMove.order + 1;
            const newsColumns = [...this.columns];
            for (const column of newsColumns) {
                if (column.order === newOrder) {
                    if (column.isMovable) {
                        column.order = columnToMove.order;
                        columnToMove.order = newOrder;
                        break;
                    } else {
                        newOrder++;
                    }
                }
            }

            this.$emit('updateColumns', newsColumns);
        },
    },
};
</script>

<style lang="scss" scoped>
.listing-settings__columns {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-3);

    &-title {
        color: var(--colors-common-text-subtle-default);
        margin: 0;
    }

    &-search {
        display: flex;
        flex-direction: column;

        &-input {
            margin-bottom: var(--spacing-1);
        }

        &-catpion {
            color: var(--colors-common-text-subtle-default);
            margin: 0;
        }
    }
    &:not(:has(.listing-settings-column-item.sortable-ghost.sortable-chosen)) {
        .listing-settings-column-item:hover:not(.listing-settings-column-item--disabled):has(.listing-settings-column-item__actions) {
            background-color: var(--colors-component-background-actionable-hover);
        }
    }
}
</style>
